










































import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import Enumerable from 'linq';
import * as api from '@/api/api';
import StatusStore from '@/models/status/statusstore';
import CharacterIcon from '@/components/parts/CharacterIcon.vue';

@Component({
  components: {
    CharacterIcon,
  },
})
export default class Map extends Vue {
  @Prop() public towns!: api.Town[];
  @Prop() public countries!: api.Country[];
  @Prop({
    default: () => new api.Town(-1),
  }) public town!: api.Town;
  @Prop({
    default: () => new api.Town(-1),
  }) public currentTown!: api.Town;
  @Prop({
    default: undefined,
  }) public store!: StatusStore;
  @Prop({
    default: 0,
  }) public mode!: number;
  @Prop({
    default: false,
  }) public isMonarchIcon!: boolean;

  private isSelected(current: api.Town): boolean {
    return this.town.id === current.id;
  }

  private getCountryName(town: api.Town): string {
    const country = Enumerable
      .from(this.countries)
      .firstOrDefault((c) => c.id === town.countryId);
    if (country !== undefined) {
      return country.name;
    } else {
      return '無所属';
    }
  }

  private getTownColor(town: api.Town): number {
    const country = Enumerable
      .from(this.countries)
      .firstOrDefault((c) => c.id === town.countryId);
    if (country !== undefined) {
      return country.colorId;
    } else {
      return 0;
    }
  }

  private getModeCharacters(town: api.Town): api.Character[] {
    if (this.mode === 1) {
      return this.getCharacters(town);
    } else {
      return this.getDefenders(town);
    }
  }

  private getModeCharactersCount(town: api.Town): number {
    if (this.mode === 3) {
      return this.getCharacters(town).length;
    } else {
      return this.getDefenders(town).length;
    }
  }

  private getCharacters(town: api.Town): api.Character[] {
    return Enumerable
      .from(this.store.characters)
      .where((c) => c.townId === town.id && c.countryId === this.store.character.countryId)
      .toArray();
  }

  private getDefenders(town: api.Town): api.Character[] {
    if (town.countryId === this.store.character.countryId) {
      return Enumerable
        .from(this.store.defenders)
        .where((d) => d.townId === town.id)
        .join(this.store.characters, (d) => d.characterId, (c) => c.id, (d, c) => c)
        .toArray();
    } else {
      return [];
    }
  }

  private getMonarchIcon(town: api.Town): api.CharacterIcon | undefined {
    if (!this.store) {
      return undefined;
    }
    const country = Enumerable
      .from(this.countries)
      .firstOrDefault((c) => c.id === town.countryId);
    if (country !== undefined) {
      const monarch = Enumerable
        .from(country.posts)
        .firstOrDefault((p) => p.type === api.CountryPost.typeMonarch);
      if (monarch) {
        const chara = Enumerable
          .from(this.store.characters)
          .firstOrDefault((c) => c.id === monarch.characterId);
        if (chara) {
          return chara.mainIcon;
        }
      }
    }
    return undefined;
  }

  private getTopTown(town: api.Town): api.Town | undefined {
    return this.towns.find((t) => t.x === town.x && t.y === town.y - 1);
  }

  private getLeftTown(town: api.Town): api.Town | undefined {
    return this.towns.find((t) => t.x === town.x - 1 && t.y === town.y);
  }

  private getRightTown(town: api.Town): api.Town | undefined {
    return this.towns.find((t) => t.x === town.x + 1 && t.y === town.y);
  }

  private getBottomTown(town: api.Town): api.Town | undefined {
    return this.towns.find((t) => t.x === town.x && t.y === town.y + 1);
  }

  private isTopTownSameCountry(town: api.Town): boolean {
    const target = this.getTopTown(town);
    return this.isSameCountry(town, target);
  }

  private isLeftTownSameCountry(town: api.Town): boolean {
    const target = this.getLeftTown(town);
    return this.isSameCountry(town, target);
  }

  private isRightTownSameCountry(town: api.Town): boolean {
    const target = this.getRightTown(town);
    return this.isSameCountry(town, target);
  }

  private isBottomTownSameCountry(town: api.Town): boolean {
    const target = this.getBottomTown(town);
    return this.isSameCountry(town, target);
  }

  private isSameCountry(t1: api.Town | undefined, t2: api.Town | undefined): boolean {
    return t1 === undefined || t2 === undefined || t1.countryId === t2.countryId;
  }

  private getModeGraphMax(town: api.Town, index: number): number {
    const townMax = this.getModeMaxValuePrivate(town, index);
    const maxMax = Enumerable
      .from(this.towns)
      .select((t) => this.getModeMaxValuePrivate(t, index))
      .where((t) => t !== undefined)
      .max((t) => t);

    return townMax / maxMax * 100;
  }

  private getModeGraphCurrent(town: api.Town, index: number): number {
    const max = this.getModeMaxValuePrivate(town, index);
    const current = this.getModeValuePrivate(town, index);
    return current / max * 100;
  }

  private isModeGraphAlmostAll(town: api.Town, index: number): boolean {
    return this.getModeGraphCurrent(town, index) >= 90;
  }

  private isModeGraphMax(town: api.Town, index: number): boolean {
    return this.getModeGraphCurrent(town, index) >= 100;
  }

  private getModeMaxValuePrivate(town: api.Town, index: number): number {
    return this.mode === 5 ? (index === 0 ? town.agricultureMax : town.commercialMax)
         : this.mode === 6 ? (index === 0 ? town.technologyMax : town.wallMax)
         : this.mode === 7 ? (index === 0 ? town.peopleMax : town.technologyMax)
         : this.mode === 8 ? (index === 0 ? town.peopleMax : index === 1 ? 100 : town.technologyMax)
         : 1;
  }

  private getModeValuePrivate(town: api.Town, index: number): number {
    return this.mode === 5 ? (index === 0 ? town.agriculture : town.commercial)
         : this.mode === 6 ? (index === 0 ? town.technology : town.wall)
         : this.mode === 7 ? (index === 0 ? town.people : town.technology)
         : this.mode === 8 ? (index === 0 ? town.people : index === 1 ? town.security : town.technology)
         : 1;
  }
}
